import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserCheck, Target, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_META = {
  self_service: { label: 'Self-Service', cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40' },
  unassigned: { label: 'Unassigned', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  sales_assisted: { label: 'Sales Assisted', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  admin_approved: { label: 'Admin Approved', cls: 'bg-green-500/20 text-green-300 border-green-500/40' },
  rejected_for_incentive: { label: 'Rejected for Incentive', cls: 'bg-red-500/20 text-red-300 border-red-500/40' },
  not_eligible: { label: 'Not Eligible', cls: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/40' },
};

export const SalesAttributionPanel = ({ order, onRefresh, isAdmin }) => {
  const navigate = useNavigate();
  const [team, setTeam] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const status = order.attribution_status || (order.source === 'shopify' ? 'self_service' : null);
  const meta = STATUS_META[status] || null;

  useEffect(() => {
    if (!assignOpen || team.length) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTeam((r.data || []).filter(u => u.is_active !== false)))
      .catch(() => {});
  }, [assignOpen, team.length]);

  const act = async (action, extra = {}) => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/sales/orders/${order.id}/attribution`,
        { action, ...extra }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message);
      const res = await axios.get(`${API}/orders/${order.id}`, { headers: { Authorization: `Bearer ${token}` } });
      onRefresh && onRefresh(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
      setAssignOpen(false);
    }
  };

  return (
    <div className="bg-wt-paper rounded-lg border border-wt-border p-4" data-testid="sales-attribution-panel">
      <h3 className="text-sm font-semibold text-wt-text-primary flex items-center gap-2 mb-3">
        <Target className="w-4 h-4 text-wt-gold" /> Sales Attribution
      </h3>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <p className="text-xs text-wt-text-muted">Sales Owner</p>
          <p className="text-wt-text-primary" data-testid="sales-owner-value">{order.sales_owner_name || '—'}</p>
        </div>
        <div>
          <p className="text-xs text-wt-text-muted">Attribution Status</p>
          {meta ? (
            <Badge className={`border text-xs ${meta.cls}`} data-testid="attribution-status-badge">{meta.label}</Badge>
          ) : <span className="text-wt-text-muted">—</span>}
        </div>
        <div>
          <p className="text-xs text-wt-text-muted">Order Purpose</p>
          <p className="text-wt-text-primary capitalize">{(order.order_purpose || '—').replace(/_/g, ' ')}</p>
        </div>
        <div>
          <p className="text-xs text-wt-text-muted">Created From</p>
          <p className="text-wt-text-primary capitalize">{(order.created_from || order.source || '—').replace(/_/g, ' ')}</p>
        </div>
        <div>
          <p className="text-xs text-wt-text-muted">Created By</p>
          <p className="text-wt-text-primary" data-testid="order-created-by">{order.created_by_name || '—'}</p>
        </div>
        <div>
          <p className="text-xs text-wt-text-muted">Incentive Eligible</p>
          <p className={order.incentive_eligible ? 'text-emerald-400' : 'text-wt-text-muted'} data-testid="incentive-eligible-value">
            {order.incentive_eligible ? 'Yes' : 'No'}
            {order.sales_error && <span className="text-red-400 ml-2">(Sales Error)</span>}
          </p>
        </div>
        <div>
          <p className="text-xs text-wt-text-muted">Discount Approval</p>
          <p className={`capitalize ${String(order.discount_approval_status || '').startsWith('pending') ? 'text-amber-400' : 'text-wt-text-primary'}`}>
            {(order.discount_approval_status || '—').replace(/_/g, ' ')}
          </p>
        </div>
        {order.payout_month && (
          <div>
            <p className="text-xs text-wt-text-muted">Payout Month</p>
            <p className="text-wt-text-primary">{order.payout_month}</p>
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 mt-2">
        {order.lead_id && (
          <button className="text-xs text-wt-gold flex items-center gap-1 hover:underline" data-testid="view-linked-lead-btn"
            onClick={() => navigate(`/leads/${order.lead_id}`)}>
            <ExternalLink className="w-3 h-3" /> View Lead
          </button>
        )}
        {order.conversation_id && (
          <button className="text-xs text-wt-gold flex items-center gap-1 hover:underline" data-testid="view-linked-conversation-btn"
            onClick={() => navigate(`/marketing/inbox?conversation=${order.conversation_id}`)}>
            <ExternalLink className="w-3 h-3" /> View Conversation
          </button>
        )}
      </div>

      {isAdmin && (
        <div className="mt-3 pt-3 border-t border-wt-border space-y-2">
          <div className="flex flex-wrap gap-2">
            {!assignOpen ? (
              <Button size="sm" variant="outline" className="border-wt-border text-wt-text-primary h-7 text-xs"
                onClick={() => setAssignOpen(true)} data-testid="assign-sales-owner-btn">
                <UserCheck className="w-3 h-3 mr-1" /> Assign Sales Owner
              </Button>
            ) : (
              <div className="flex items-center gap-2 w-full">
                <Select onValueChange={(v) => act('assign_owner', { sales_owner_id: v === '__clear__' ? null : v })}>
                  <SelectTrigger className="h-8 bg-wt-bg border-wt-border text-wt-text-primary text-xs flex-1" data-testid="sales-owner-select">
                    <SelectValue placeholder="Select employee..." />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border">
                    {order.sales_owner_id && <SelectItem value="__clear__">— Clear owner —</SelectItem>}
                    {team.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setAssignOpen(false)}>Cancel</Button>
              </div>
            )}
            <Select onValueChange={(v) => act('set_purpose', { order_purpose: v })}>
              <SelectTrigger className="h-7 w-auto bg-wt-bg border-wt-border text-wt-text-primary text-xs" data-testid="attribution-purpose-select">
                <SelectValue placeholder="Set Purpose" />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                {['new_sale', 'replacement', 'exchange', 'warranty', 'service', 'correction', 'internal'].map(p => (
                  <SelectItem key={p} value={p} className="capitalize">{p.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={busy} className="bg-green-500/20 text-green-300 border border-green-500/40 h-7 text-xs hover:bg-green-500/30"
              onClick={() => act('approve')} data-testid="approve-attribution-btn">Approve Attribution</Button>
            <Button size="sm" disabled={busy} className="bg-red-500/15 text-red-300 border border-red-500/40 h-7 text-xs hover:bg-red-500/25"
              onClick={() => act('reject')} data-testid="reject-incentive-btn">Reject Incentive</Button>
            <Button size="sm" disabled={busy} variant="outline" className="border-wt-border text-wt-text-muted h-7 text-xs"
              onClick={() => act('not_eligible')} data-testid="mark-not-eligible-btn">Not Eligible</Button>
            <Button size="sm" disabled={busy} variant="outline"
              className={`h-7 text-xs ${order.sales_error ? 'border-green-500/40 text-green-300' : 'border-red-500/40 text-red-300'}`}
              onClick={() => act('sales_error', { sales_error: !order.sales_error })} data-testid="mark-sales-error-btn">
              {order.sales_error ? 'Clear Sales Error' : 'Mark Sales Error'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesAttributionPanel;
